import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { faqJsonLd } from "@/components/site/FaqSection";
import { homeFaqs } from "@/content/faqs";
import heroLogoPhone from "@/assets/hero-v1.webp.asset.json";
import heroSubject from "@/assets/hero-v2.webp.asset.json";
import heroVoice from "@/assets/hero-v3.webp.asset.json";
import heroLogoPhoneSm from "@/assets/hero-v1-sm.webp.asset.json";
import heroSubjectSm from "@/assets/hero-v2-sm.webp.asset.json";
import heroVoiceSm from "@/assets/hero-v3-sm.webp.asset.json";

const SiteFooter = lazy(() =>
  import("@/components/site/SiteFooter").then((m) => ({ default: m.SiteFooter })),
);
const FeatureGridSection = lazy(() =>
  import("@/components/site/sections").then((m) => ({ default: m.FeatureGridSection })),
);
const TikTokExplainerSection = lazy(() =>
  import("@/components/site/sections").then((m) => ({ default: m.TikTokExplainerSection })),
);
const BuiltDifferentlySection = lazy(() =>
  import("@/components/site/sections").then((m) => ({ default: m.BuiltDifferentlySection })),
);
const ProductReleasesSection = lazy(() =>
  import("@/components/site/sections").then((m) => ({ default: m.ProductReleasesSection })),
);
const ConnectedPortalsPreview = lazy(() =>
  import("@/components/site/sections").then((m) => ({ default: m.ConnectedPortalsPreview })),
);
const TestimonialsSection = lazy(() =>
  import("@/components/site/sections").then((m) => ({ default: m.TestimonialsSection })),
);
const FaqSection = lazy(() =>
  import("@/components/site/FaqSection").then((m) => ({ default: m.FaqSection })),
);

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "adaptED | South Africa's AI Learning Companion & Matric Prep" },
      {
        name: "description",
        content:
          "We're building South Africa's frontier AI-native and CAPS-native learning companion for Grades 10-12, with adaptive, personalised matric exam support.",
      },
      {
        name: "keywords",
        content:
          "South African AI learning companion, CAPS-native learning, CAPS-aligned learning, matric exam preparation, how to pass matric, how to get a Bachelor's pass, Grade 10 learning support, Grade 11 learning support, Grade 12 exam preparation, matric study app, personalised learning, knowledge-gap analysis and exam readiness",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "adaptED | South Africa's AI Learning Companion" },
      {
        property: "og:description",
        content:
          "South Africa's frontier AI-native and CAPS-native learning companion for Grades 10-12, offering adaptive support and matric exam preparation.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "adaptED | South Africa's AI Learning Companion" },
      {
        name: "twitter:description",
        content:
          "South Africa's frontier AI-native and CAPS-native learning companion for Grades 10-12, offering adaptive support and matric exam preparation.",
      },
      { property: "og:image", content: "https://www.adapted.info/__l5e/assets-v1/ddf459f9-9bda-4f99-8b5a-5df16e601132/hero-3.png" },
      { property: "og:image:alt", content: "adaptED, South Africa's adaptive AI learning companion for Grades 10-12." },
      { name: "twitter:image", content: "https://www.adapted.info/__l5e/assets-v1/ddf459f9-9bda-4f99-8b5a-5df16e601132/hero-3.png" },
      { name: "twitter:image:alt", content: "adaptED, South Africa's adaptive AI learning companion for Grades 10-12." },
    ],
    links: [
      { rel: "canonical", href: "https://www.adapted.info/" },
      {
        rel: "preload",
        as: "image",
        href: heroVoiceSm.url,
        imageSrcSet: `${heroVoiceSm.url} 300w, ${heroVoice.url} 427w`,
        imageSizes: "200px",
        fetchPriority: "high",
      },

    ],
    scripts: [faqJsonLd(homeFaqs, "https://www.adapted.info/")],
  }),
  component: Index,
});

function Index() {
  return (
    <main id="top">
      <section className="surface-ink relative overflow-hidden pb-16 pt-32">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="text-center lg:text-left">
              <h1 className="font-display text-5xl leading-[0.95] text-ink-foreground sm:text-6xl md:text-7xl">
                A learning companion
                <br />
                that adapts to you
              </h1>
              <p className="mx-auto mt-6 max-w-xl text-base text-ink-foreground/80 lg:mx-0">
                We are building safe, learning-first AI technology for South African learners, designed for better results today and greater possibilities tomorrow.
              </p>

              <div className="mt-4 flex justify-center lg:justify-start">
                <span className="rounded-md border border-ink-foreground/10 bg-ink-foreground/[0.06] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-foreground/90 sm:backdrop-blur-md">
                  CURRENT FOCUS: GRADE 10-12 LEARNERS
                </span>
              </div>

              <div className="mt-6 flex flex-col items-center justify-center gap-6 sm:flex-row lg:justify-start">
                <a
                  href="https://www.quizzyonline.co.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
                >
                  Check your exam readiness now
                </a>
              </div>

              <div className="mt-10 text-center lg:text-left">
                <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-end sm:justify-center lg:justify-start">
                  <div className="flex flex-col items-center sm:items-start">
                    <div className="flex -space-x-2.5">
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          aria-hidden="true"
                          className="size-9 rounded-full border-2 border-ink bg-muted-foreground/60 shadow-md"
                        />
                      ))}
                      <span className="flex size-9 items-center justify-center rounded-full border-2 border-ink bg-primary text-[0.6875rem] font-bold text-primary-foreground shadow-md">
                        +34K
                      </span>
                    </div>
                    <div className="mt-4 space-y-0.5">
                      <div className="font-display text-5xl font-black leading-none tracking-tight text-ink-foreground sm:text-6xl">
                         34,700<span className="text-primary-on-ink">+</span>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-foreground/75">
                        Learners reached via{" "}
                        <a
                          href="https://www.tiktok.com/@adapted_sa"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-ink-foreground underline decoration-primary/60 underline-offset-2 transition-colors hover:text-primary-on-ink"
                        >
                          <svg
                            className="size-3.5 shrink-0"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                          </svg>
                          TikTok
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="grid w-full min-w-0 grid-cols-3 gap-4 lg:min-w-[26rem] xl:min-w-[30rem] lg:gap-5">
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-ink-foreground/10 bg-ink-foreground/[0.03] px-4 py-6 transition-colors sm:backdrop-blur-sm hover:bg-ink-foreground/[0.06] sm:px-5 lg:px-6">
                      <span className="font-display text-2xl font-bold leading-none text-ink-foreground sm:text-3xl">
                         7M<span className="text-primary-on-ink">+</span>
                      </span>
                      <span className="mt-2.5 text-center text-[0.6875rem] font-bold uppercase leading-tight tracking-[0.1em] text-primary-on-ink">
                        LEARNER IMPRESSIONS
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-ink-foreground/10 bg-ink-foreground/[0.03] px-4 py-6 transition-colors sm:backdrop-blur-sm hover:bg-ink-foreground/[0.06] sm:px-5 lg:px-6">
                      <div className="flex items-center gap-1">
                        <span className="font-display text-2xl font-bold leading-none text-ink-foreground sm:text-3xl">
                          4.9
                        </span>
                        <svg
                          className="size-4 fill-primary text-primary-on-ink"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                      <span className="mt-2.5 text-center text-[0.6875rem] font-bold uppercase leading-tight tracking-[0.1em] text-primary-on-ink">
                        Avg Rating
                      </span>
                    </div>
                    <div className="flex flex-col items-center justify-center rounded-2xl border border-ink-foreground/10 bg-ink-foreground/[0.03] px-4 py-6 transition-colors sm:backdrop-blur-sm hover:bg-ink-foreground/[0.06] sm:px-5 lg:px-6">
                      <span className="font-display text-2xl font-bold leading-none text-ink-foreground sm:text-3xl">
                         245,9<span className="text-primary-on-ink">K</span>
                      </span>
                      <span className="mt-2.5 text-center text-[0.6875rem] font-bold uppercase leading-tight tracking-[0.1em] text-primary-on-ink">
                        <a
                          href="https://www.tiktok.com/@adapted_sa"
                          target="_blank"
                          rel="noreferrer"
                          className="transition-colors hover:text-ink-foreground"
                        >
                          TikTok<br />Likes
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 -mt-10 mx-auto w-full max-w-xl lg:-mt-16">
              <div className="pointer-events-none absolute -bottom-2 left-1/2 h-16 w-[70%] -translate-x-1/2 rounded-[100%] bg-primary/25 blur-3xl" />

              <div className="relative flex items-center justify-center lg:translate-x-10">
                <img
                  src={heroLogoPhone.url}
                  srcSet={`${heroLogoPhoneSm.url} 200w, ${heroLogoPhone.url} 460w`}
                  sizes="200px"
                  alt="adaptED app splash screen on a phone"
                  width={460}
                  height={981}
                  decoding="async"
                  className="w-[30%] -rotate-6 translate-y-8 drop-shadow-2xl"
                />
                <img
                  src={heroVoice.url}
                  srcSet={`${heroVoiceSm.url} 300w, ${heroVoice.url} 427w`}
                  sizes="200px"
                  alt="Voice knowledge check screen in the adaptED app"
                  width={427}
                  height={975}
                  decoding="async"
                  fetchPriority="high"
                  className="relative z-10 -mx-[9%] w-[42%] drop-shadow-[0_35px_60px_rgba(0,0,0,0.45)]"
                />
                <img
                  src={heroSubject.url}
                  srcSet={`${heroSubjectSm.url} 200w, ${heroSubject.url} 394w`}
                  sizes="200px"
                  alt="Subject selection screen in the adaptED app"
                  width={394}
                  height={834}
                  decoding="async"
                  className="w-[30%] rotate-6 translate-y-8 drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <TikTokExplainerSection />
      </Suspense>

      <Suspense fallback={null}>
        <FeatureGridSection />
      </Suspense>

      <Suspense fallback={null}>
        <BuiltDifferentlySection />
        <ProductReleasesSection />
        <ConnectedPortalsPreview />
      </Suspense>

      <Suspense fallback={null}>
        <TestimonialsSection />
        <FaqSection
          items={homeFaqs}
          paragraph="What learners, parents and schools ask us most about adaptED."
        />
        <SiteFooter />
      </Suspense>
    </main>
  );
}
